import { useRef } from 'react';
import Grid from '@mui/material/Grid2';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Save from '@mui/icons-material/Save';
import Cancel from '@mui/icons-material/Cancel';
import PropTypes from 'prop-types';

const SignChangeComponent = ({
  open,
  setOpen,
  images,
  handleSearch,
  searchTerm,
  handleCancelSearch,
  selectedSign,
  setSelectedSign,
  replaceSign,
  isAddSign,
  activeStep,
}) => {
  const scrollContainerRef = useRef(null);

  // Navigation functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setSelectedSign(null);
  };

  return (
    <Grid item size={{ xs: 12 }}>
      <Fade in={open} timeout={{ enter: 500, exit: 300 }}>
        <Box
          sx={{
            opacity: isAddSign && activeStep !== 2 && 0.6,
            filter: isAddSign && activeStep !== 2 && 'saturate(0.3) brightness(0.8)',
            pointerEvents: isAddSign && activeStep !== 2 && 'none',
            border: isAddSign && activeStep !== 2 && '1px dashed',
            transition: 'filter 0.3s ease',
            '&::before':
              isAddSign && activeStep !== 2
                ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `repeating-linear-gradient(
              45deg,
              rgba(200, 200, 200, 0.4),
              rgba(200, 200, 200, 0.4) 15px,
              rgba(220, 220, 220, 0.3) 15px,
              rgba(220, 220, 220, 0.3) 30px
            )`,
                    backgroundSize: '42px 42px',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }
                : {},
            '&::after':
              isAddSign && activeStep !== 2
                ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-50%',
                    width: '50%',
                    height: '100%',
                    background:
                      'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
                    pointerEvents: 'none',
                    zIndex: 2,
                  }
                : {},
          }}
        >
          <Collapse in={open} timeout={500}>
            <Paper
              sx={{
                p: 3,
                transform: open ? 'translateY(0)' : 'translateY(-20px)',
                transition: 'transform 0.4s ease-out',
                opacity: open ? 1 : 0,
                mb: 2,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
              }}
            >
              {/* Search Box */}
              <Box mb={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search for sign using sign name or road standard code"
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleCancelSearch} edge="end" size="small">
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>

              {/* Thumbnail Navigation */}
              <Box position="relative" mb={1}>
                {/* Left Arrow */}
                <IconButton
                  onClick={scrollLeft}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                  }}
                  aria-label="Scroll left"
                >
                  <ChevronLeft />
                </IconButton>

                {/* Thumbnails */}
                <Box
                  ref={scrollContainerRef}
                  sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    py: 1,
                    px: 8,
                    gap: 2,
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    minHeight: '100px',
                  }}
                >
                  {images?.length > 0 ? (
                    images.map((image) => (
                      <Fade key={image.imageID} in>
                        <Box
                          onClick={() => setSelectedSign(image)}
                          title={`Category: ${image.category}\nDescription: ${image.description}\nRoad Standard Code: ${image.roadStandardCode}`}
                          sx={{
                            flexShrink: 0,
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            borderRadius: '8px',
                            backgroundColor: '#F0F0F0',
                            border:
                              selectedSign && selectedSign.imageID === image.imageID
                                ? '2px solid #1976d2'
                                : '1px solid #e0e0e0',
                            transform:
                              selectedSign && selectedSign.imageID === image.imageID
                                ? 'scale(1.05)'
                                : 'scale(1)',
                            '&:hover': {
                              borderColor:
                                selectedSign && selectedSign.imageID === image.imageID
                                  ? '#1976d2'
                                  : '#bdbdbd',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            },
                          }}
                        >
                          <Box position="relative">
                            <img
                              src={image.imageFile}
                              alt={image.description}
                              style={{
                                height: '100px',
                                width: '100px',
                                objectFit: 'contain',
                              }}
                            />
                            <Typography
                              variant="caption"
                              title={`Category: ${image.category}\nDescription: ${image.description}\nRoad Standard Code: ${image.roadStandardCode}`}
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                padding: '2px',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                              }}
                            >
                              {image.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Fade>
                    ))
                  ) : (
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        minHeight: '100px',
                      }}
                    >
                      <Typography>{`No images found matching ${searchTerm}`}</Typography>
                    </Box>
                  )}
                </Box>

                {/* Right Arrow */}
                <IconButton
                  onClick={scrollRight}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                  }}
                  aria-label="Scroll right"
                >
                  <ChevronRight />
                </IconButton>
              </Box>

              {/* Action Buttons */}
              {!isAddSign && (
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleCancel}
                    startIcon={<Cancel />}
                    size="small"
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => replaceSign()}
                    startIcon={<Save />}
                    disabled={!selectedSign}
                    size="small"
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Paper>
          </Collapse>
        </Box>
      </Fade>
    </Grid>
  );
};

export default SignChangeComponent;

SignChangeComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  images: PropTypes.array.isRequired,
  handleSearch: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  handleCancelSearch: PropTypes.func.isRequired,
  selectedSign: PropTypes.object.isRequired,
  setSelectedSign: PropTypes.func.isRequired,
  replaceSign: PropTypes.func.isRequired,
  isAddSign: PropTypes.bool.isRequired,
  activeStep: PropTypes.number.isRequired,
};
